import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fmtCurrency } from '../lib/display-labels';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Search,
  X,
  Building2,
  FileText,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  Square,
  Hash,
  Calendar,
  Loader2,
  Settings2,
  ClipboardList,
  DollarSign,
  Zap,
  AlertCircle,
} from 'lucide-react';
import { api } from '../api/client';
import { ticketIsInvoiceable } from '../lib/ticket-status';
import { readTaxDiffersWarning, type InvoiceTaxWarning } from '../lib/invoice-tax-warning';
import { InvoiceTaxWarningNotice } from '../components/invoices/InvoiceTaxWarningNotice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Customer {
  id: string;
  name: string;
  mainContactName?: string;
  main_contact_name?: string;
  city?: string;
  provinceState?: string;
  province_state?: string;
  paymentTerms?: string;
  payment_terms?: string;
}

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  unit?: string;
}

interface Ticket {
  id: string;
  ticketNumber?: string;
  ticket_number?: string;
  date: string;
  status: string;
  customerId?: string;
  customer_id?: string;
  customer?: string;
  rigName?: string;
  rig_name?: string;
  subtotal?: number;
  total?: number;
  lineItems?: LineItem[];
  line_items?: LineItem[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STEP_LABELS = ['Select Client', 'Select Tickets', 'Options', 'Review'];

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors';

// ---------------------------------------------------------------------------
// Helper: format currency
// ---------------------------------------------------------------------------
/**
 * @see fmtCurrency — it owns the '$', which is why the call sites below no
 * longer prefix one. A caller-supplied '$' would render an absent amount as
 * '$—'.
 */
const fmt = (n: number | string | null | undefined) => fmtCurrency(n);

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================
export default function InvoiceGenerateScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const passedTicketIds: string[] = (location.state as any)?.ticketIds || [];

  // --- Wizard state ---
  const [step, setStep] = useState(passedTicketIds.length > 0 ? 3 : 1);
  const [autoSetupDone, setAutoSetupDone] = useState(false);

  // Step 1
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');

  // Step 2
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set());
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<Set<string>>(new Set());
  const [expandedTicketIds, setExpandedTicketIds] = useState<Set<string>>(new Set());

  // Step 3
  const [groupLineItems, setGroupLineItems] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [notes, setNotes] = useState('');

  // Toast
  const [toast, setToast] = useState('');

  // A generate that came back with the API's TAX_DIFFERS_FROM_TICKETS warning.
  // The invoice exists; the screen stays here so the warning is read, and the
  // notice lives in component state, so it lasts until dismissed or the page is
  // left. With no warning the screen goes straight to the invoice, as before.
  const [taxResult, setTaxResult] = useState<{
    invoiceId: string | null;
    invoiceNumber: string | null;
    warning: InvoiceTaxWarning;
  } | null>(null);
  const [taxWarningDismissed, setTaxWarningDismissed] = useState(false);
  // Focus the result heading when it appears, so a screen reader lands on the
  // result and reads on into the notice rather than missing a region that was
  // mounted together with its content.
  const resultHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (taxResult) resultHeadingRef.current?.focus();
  }, [taxResult]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  // --- Data fetching ---
  const { data: customers, isLoading: customersLoading } = useQuery<Customer[]>({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.customers ?? []);
    }),
    staleTime: 0,
  });

  const { data: allTickets, isLoading: ticketsLoading } = useQuery<Ticket[]>({
    queryKey: ['tickets'],
    queryFn: () => api.tickets.list().then((r) => r.data.data ?? []),
    enabled: !!selectedCustomerId || passedTicketIds.length > 0,
    staleTime: 0,
  });

  // Auto-generate: when coming from a ticket, skip wizard and generate immediately
  useEffect(() => {
    if (autoSetupDone || passedTicketIds.length === 0 || !allTickets?.length) return;
    const ticket = allTickets.find((t) => t.id === passedTicketIds[0]);
    if (ticket) {
      const custId = ticket.customerId || ticket.customer_id;
      if (!custId) return;
      setAutoSetupDone(true);

      // Generate invoice directly — no wizard
      generateMutation.mutate({
        customerId: custId,
        ticketIds: passedTicketIds,
        groupLineItems: false,
        paymentTerms: 'Net 30',
        notes: '',
      });
    }
  }, [allTickets, passedTicketIds, autoSetupDone]);

  // --- Derived data ---
  const filteredCustomers = useMemo(() => {
    if (!customers) return [];
    if (!customerSearch.trim()) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.mainContactName || c.main_contact_name || '').toLowerCase().includes(q) ||
        (c.city || '').toLowerCase().includes(q),
    );
  }, [customers, customerSearch]);

  const selectedCustomer = useMemo(
    () => customers?.find((c) => c.id === selectedCustomerId) ?? null,
    [customers, selectedCustomerId],
  );

  const customerTickets = useMemo(() => {
    if (!allTickets || !selectedCustomerId) return [];
    return allTickets.filter((t) => {
      const custId = t.customerId || t.customer_id;
      const isCustomerMatch = custId === selectedCustomerId;
      return isCustomerMatch && ticketIsInvoiceable(t.status);
    });
  }, [allTickets, selectedCustomerId]);

  const selectedTickets = useMemo(
    () => customerTickets.filter((t) => selectedTicketIds.has(t.id)),
    [customerTickets, selectedTicketIds],
  );

  const estimatedSubtotal = useMemo(
    () => selectedTickets.reduce((sum, t) => sum + (t.subtotal || t.total || 0), 0),
    [selectedTickets],
  );

  // --- Generate mutation ---
  const generateMutation = useMutation({
    mutationFn: (payload: {
      customerId: string;
      ticketIds: string[];
      lineItemIds?: string[];
      groupLineItems: boolean;
      paymentTerms: string;
      notes: string;
    }) => api.invoices.generate(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      const invoiceId = res.data?.data?.id;
      const taxWarning = readTaxDiffersWarning(res.data);
      if (taxWarning) {
        const inv = res.data?.data ?? {};
        setTaxWarningDismissed(false);
        setTaxResult({
          invoiceId: invoiceId ?? null,
          invoiceNumber: inv.invoiceNumber ?? inv.invoice_number ?? null,
          warning: taxWarning,
        });
        // Drop the `ticketIds` history state. The reader now stays on this
        // entry, and a reload would otherwise re-run the from-a-ticket
        // auto-generate against tickets that are already invoiced.
        navigate(location.pathname, { replace: true, state: null });
        return;
      }
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}`, { replace: true });
      } else {
        navigate('/invoices', { replace: true });
      }
    },
    onError: (err: any) => {
      showToast(err.response?.data?.error || 'Failed to generate invoice');
    },
  });

  // --- Handlers ---
  function handleSelectCustomer(id: string) {
    setSelectedCustomerId(id);
    // Reset downstream state
    setSelectedTicketIds(new Set());
    setSelectedLineItemIds(new Set());
    setExpandedTicketIds(new Set());
  }

  function toggleTicket(ticketId: string) {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
        // Also remove any line items for this ticket
        const ticket = customerTickets.find((t) => t.id === ticketId);
        const items = ticket?.lineItems || ticket?.line_items || [];
        setSelectedLineItemIds((prevLi) => {
          const nextLi = new Set(prevLi);
          items.forEach((li) => nextLi.delete(li.id));
          return nextLi;
        });
      } else {
        next.add(ticketId);
        // Select all line items for this ticket by default
        const ticket = customerTickets.find((t) => t.id === ticketId);
        const items = ticket?.lineItems || ticket?.line_items || [];
        if (items.length > 0) {
          setSelectedLineItemIds((prevLi) => {
            const nextLi = new Set(prevLi);
            items.forEach((li) => nextLi.add(li.id));
            return nextLi;
          });
        }
      }
      return next;
    });
  }

  function toggleLineItem(lineItemId: string) {
    setSelectedLineItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineItemId)) {
        next.delete(lineItemId);
      } else {
        next.add(lineItemId);
      }
      return next;
    });
  }

  function handleSelectAll() {
    if (selectedTicketIds.size === customerTickets.length) {
      // Deselect all
      setSelectedTicketIds(new Set());
      setSelectedLineItemIds(new Set());
    } else {
      // Select all
      const allIds = new Set(customerTickets.map((t) => t.id));
      setSelectedTicketIds(allIds);
      const allLiIds = new Set<string>();
      customerTickets.forEach((t) => {
        const items = t.lineItems || t.line_items || [];
        items.forEach((li) => allLiIds.add(li.id));
      });
      setSelectedLineItemIds(allLiIds);
    }
  }

  function toggleExpanded(ticketId: string) {
    setExpandedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  }

  function handleGenerate() {
    if (!selectedCustomerId || selectedTicketIds.size === 0) return;
    const liIds = Array.from(selectedLineItemIds);
    generateMutation.mutate({
      customerId: selectedCustomerId,
      ticketIds: Array.from(selectedTicketIds),
      ...(liIds.length > 0 ? { lineItemIds: liIds } : {}),
      groupLineItems,
      paymentTerms,
      notes,
    });
  }

  // --- Navigation guards ---
  function canGoNext(): boolean {
    switch (step) {
      case 1:
        return !!selectedCustomerId;
      case 2:
        return selectedTicketIds.size > 0;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  }

  // --- Progress ---
  const progressPct = (step / 4) * 100;

  // =========================================================================
  // If auto-generating from ticket, show loading
  if (passedTicketIds.length > 0 && !autoSetupDone) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="text-fieldiq-accent animate-spin mx-auto mb-3" />
          <p className="text-white font-medium">Generating invoice...</p>
        </div>
      </div>
    );
  }

  if (taxResult) {
    return (
      <div data-testid="invoice-generate-result" className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark">
        <div className="border-b border-border px-6 py-4">
          <h1 ref={resultHeadingRef} tabIndex={-1} className="text-base font-bold text-foreground outline-none">Invoice created</h1>
          <p className="text-xs text-fieldiq-text">
            {taxResult.invoiceNumber ? `Invoice ${taxResult.invoiceNumber} has been created.` : 'The invoice has been created.'}
          </p>
        </div>
        <div className="max-w-[1920px] mx-auto px-4 py-6 space-y-4">
          {!taxWarningDismissed && (
            <InvoiceTaxWarningNotice
              warning={taxResult.warning}
              onDismiss={() => setTaxWarningDismissed(true)}
            />
          )}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() =>
                navigate(taxResult.invoiceId ? `/invoices/${taxResult.invoiceId}` : '/invoices', { replace: true })
              }
              className="fi-action fi-action--standard fi-action--primary"
            >
              Open invoice
            </button>
            <button
              type="button"
              onClick={() => navigate('/invoices', { replace: true })}
              className="rounded-xl px-4 py-2 text-sm font-semibold bg-fieldiq-card border border-fieldiq-border text-fieldiq-text hover:bg-fieldiq-dark/50"
            >
              All invoices
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (passedTicketIds.length > 0 && generateMutation.isPending) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="text-fieldiq-accent animate-spin mx-auto mb-3" />
          <p className="text-white font-medium">Creating invoice...</p>
        </div>
      </div>
    );
  }

  // RENDER
  // =========================================================================
  return (
    <div className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark">
      {/* ---- Header ---- */}
      <div className="border-b border-border px-6 py-4">
        <button
          onClick={() => {
            if (passedTicketIds.length > 0) { navigate(-1); return; }
            step > 1 ? setStep(step - 1) : navigate(-1);
          }}
          className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        >
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">Generate Invoice</h1>
            <p className="text-xs text-fieldiq-text">
              Step {step} of 4 &mdash; {STEP_LABELS[step - 1]}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-fieldiq-border mt-3">
          <div
            className="h-full bg-fieldiq-accent-signal transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
      {/* ---- Step Content ---- */}
      <div className="max-w-[1920px] mx-auto px-4 py-6">
        {step === 1 && (
          <StepSelectCustomer
            customers={filteredCustomers}
            loading={customersLoading}
            search={customerSearch}
            onSearchChange={setCustomerSearch}
            selectedId={selectedCustomerId}
            onSelect={handleSelectCustomer}
          />
        )}
        {step === 2 && (
          <StepSelectTickets
            tickets={customerTickets}
            loading={ticketsLoading}
            selectedTicketIds={selectedTicketIds}
            selectedLineItemIds={selectedLineItemIds}
            expandedTicketIds={expandedTicketIds}
            onToggleTicket={toggleTicket}
            onToggleLineItem={toggleLineItem}
            onToggleExpanded={toggleExpanded}
            onSelectAll={handleSelectAll}
          />
        )}
        {step === 3 && (
          <StepOptions
            groupLineItems={groupLineItems}
            onGroupLineItemsChange={setGroupLineItems}
            paymentTerms={paymentTerms}
            onPaymentTermsChange={setPaymentTerms}
            notes={notes}
            onNotesChange={setNotes}
          />
        )}
        {step === 4 && (
          <StepReview
            customer={selectedCustomer}
            tickets={selectedTickets}
            estimatedSubtotal={estimatedSubtotal}
            groupLineItems={groupLineItems}
            paymentTerms={paymentTerms}
            notes={notes}
          />
        )}
      </div>

      {/* ---- Bottom Action Bar ---- */}
      <div className="border-t border-fieldiq-border p-4 mt-4">
        <div className="max-w-[1920px] mx-auto flex gap-3">
          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canGoNext()}
              className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
                canGoNext()
                  ? 'bg-fieldiq-accent text-black hover:bg-amber-400'
                  : 'bg-fieldiq-accent text-black opacity-50 cursor-not-allowed'
              }`}
            >
              Next
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={generateMutation.isPending || selectedTicketIds.size === 0}
              className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
                generateMutation.isPending
                  ? 'bg-fieldiq-accent text-black opacity-50 cursor-not-allowed'
                  : 'bg-fieldiq-accent text-black hover:bg-amber-400'
              }`}
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap size={16} />
                  Generate Invoice
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ---- Toast ---- */}
      {toast && (
        <div className="fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2.5 rounded-xl shadow-lg text-sm animate-fade-in z-50 flex items-center gap-2">
          <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
          {toast}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// STEP 1 — Select Customer
// ===========================================================================
function StepSelectCustomer({
  customers,
  loading,
  search,
  onSearchChange,
  selectedId,
  onSelect,
}: {
  customers: Customer[];
  loading: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search customers..."
          className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl pl-10 pr-8 py-3 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:ring-1 focus:ring-fieldiq-accent"
          autoFocus
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-muted hover:text-white"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-fieldiq-card rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-fieldiq-dark" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-fieldiq-dark rounded w-1/3" />
                  <div className="h-3 bg-fieldiq-dark rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && customers.length === 0 && (
        <div className="text-center py-16">
          <Building2 size={48} className="mx-auto text-fieldiq-muted mb-4" />
          <p className="text-fieldiq-text text-lg mb-1">No customers found</p>
          <p className="text-fieldiq-muted text-sm">Try adjusting your search.</p>
        </div>
      )}

      {/* Customer list */}
      {!loading && customers.length > 0 && (
        <div className="space-y-2">
          {customers.map((customer) => {
            const isSelected = customer.id === selectedId;
            const contact = customer.mainContactName || customer.main_contact_name;
            const location = [customer.city, customer.provinceState || customer.province_state]
              .filter(Boolean)
              .join(', ');
            const terms = customer.paymentTerms || customer.payment_terms;

            return (
              <button
                key={customer.id}
                onClick={() => onSelect(customer.id)}
                className={`w-full text-left bg-fieldiq-card rounded-xl p-4 border transition-all ${
                  isSelected
                    ? 'ring-2 ring-fieldiq-accent border-fieldiq-accent'
                    : 'border-fieldiq-border hover:border-fieldiq-muted'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-fieldiq-accent/20' : 'bg-fieldiq-dark'
                    }`}
                  >
                    <Building2
                      size={20}
                      className={isSelected ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate">{customer.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {contact && (
                        <span className="text-xs text-fieldiq-text truncate">{contact}</span>
                      )}
                      {location && (
                        <span className="text-xs text-fieldiq-muted truncate">{location}</span>
                      )}
                    </div>
                  </div>
                  {terms && (
                    <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium">
                      {terms}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// STEP 2 — Select Tickets
// ===========================================================================
function StepSelectTickets({
  tickets,
  loading,
  selectedTicketIds,
  selectedLineItemIds,
  expandedTicketIds,
  onToggleTicket,
  onToggleLineItem,
  onToggleExpanded,
  onSelectAll,
}: {
  tickets: Ticket[];
  loading: boolean;
  selectedTicketIds: Set<string>;
  selectedLineItemIds: Set<string>;
  expandedTicketIds: Set<string>;
  onToggleTicket: (id: string) => void;
  onToggleLineItem: (id: string) => void;
  onToggleExpanded: (id: string) => void;
  onSelectAll: () => void;
}) {
  const allSelected = tickets.length > 0 && selectedTicketIds.size === tickets.length;

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-fieldiq-text">
          {tickets.length} office-approved ticket{tickets.length !== 1 ? 's' : ''} available
        </p>
        {tickets.length > 0 && (
          <button
            onClick={onSelectAll}
            className="text-xs font-medium text-fieldiq-accent hover:text-amber-400 transition-colors"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
        </div>
      )}

      {/* Empty */}
      {!loading && tickets.length === 0 && (
        <div className="text-center py-16">
          <ClipboardList size={48} className="mx-auto text-fieldiq-muted mb-4" />
          <p className="text-fieldiq-text text-lg mb-1">No eligible tickets</p>
          <p className="text-fieldiq-muted text-sm">
            Only tickets with status "office approved" can be invoiced.
          </p>
        </div>
      )}

      {/* Ticket list */}
      {!loading && tickets.length > 0 && (
        <div className="space-y-2">
          {tickets.map((ticket) => {
            const isSelected = selectedTicketIds.has(ticket.id);
            const isExpanded = expandedTicketIds.has(ticket.id);
            const ticketNum = ticket.ticketNumber || ticket.ticket_number || ticket.id;
            const rig = ticket.rigName || ticket.rig_name;
            const subtotal = ticket.subtotal || ticket.total || 0;
            const lineItems = ticket.lineItems || ticket.line_items || [];

            return (
              <div
                key={ticket.id}
                className={`bg-fieldiq-card rounded-xl border transition-all ${
                  isSelected
                    ? 'ring-2 ring-fieldiq-accent border-fieldiq-accent'
                    : 'border-fieldiq-border'
                }`}
              >
                {/* Ticket card header */}
                <div className="p-4 flex items-start gap-3">
                  {/* Checkbox */}
                  <button
                    onClick={() => onToggleTicket(ticket.id)}
                    className="mt-0.5 flex-shrink-0"
                  >
                    {isSelected ? (
                      <CheckSquare size={20} className="text-fieldiq-accent" />
                    ) : (
                      <Square size={20} className="text-fieldiq-muted" />
                    )}
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Hash size={13} className="text-fieldiq-muted" />
                        <span className="text-sm font-bold text-white">{ticketNum}</span>
                      </div>
                      <span className="text-sm font-bold text-green-400">{fmt(subtotal)}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-fieldiq-text flex items-center gap-1">
                        <Calendar size={11} />
                        {ticket.date}
                      </span>
                      {rig && (
                        <span className="text-xs text-fieldiq-muted truncate">{rig}</span>
                      )}
                    </div>
                  </div>

                  {/* Expand toggle (only if has line items) */}
                  {lineItems.length > 0 && (
                    <button
                      onClick={() => onToggleExpanded(ticket.id)}
                      className="mt-0.5 flex-shrink-0 p-1 rounded hover:bg-fieldiq-dark transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp size={16} className="text-fieldiq-muted" />
                      ) : (
                        <ChevronDown size={16} className="text-fieldiq-muted" />
                      )}
                    </button>
                  )}
                </div>

                {/* Expanded line items */}
                {isExpanded && lineItems.length > 0 && (
                  <div className="border-t border-fieldiq-border px-4 py-3 space-y-2">
                    <p className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide mb-2">
                      Line Items
                    </p>
                    {lineItems.map((li) => {
                      const liSelected = selectedLineItemIds.has(li.id);
                      return (
                        <div
                          key={li.id}
                          className="flex items-center gap-3 py-1.5"
                        >
                          <button
                            onClick={() => onToggleLineItem(li.id)}
                            className="flex-shrink-0"
                          >
                            {liSelected ? (
                              <CheckSquare size={16} className="text-fieldiq-accent" />
                            ) : (
                              <Square size={16} className="text-fieldiq-muted" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{li.description}</p>
                            <p className="text-xs text-fieldiq-muted">
                              {li.quantity} {li.unit || 'x'} @ {fmt(li.rate)}
                            </p>
                          </div>
                          <span className="text-sm font-medium text-white flex-shrink-0">
                            {fmt(li.amount)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// STEP 3 — Options
// ===========================================================================
function StepOptions({
  groupLineItems,
  onGroupLineItemsChange,
  paymentTerms,
  onPaymentTermsChange,
  notes,
  onNotesChange,
}: {
  groupLineItems: boolean;
  onGroupLineItemsChange: (v: boolean) => void;
  paymentTerms: string;
  onPaymentTermsChange: (v: string) => void;
  notes: string;
  onNotesChange: (v: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <Settings2 size={18} className="text-fieldiq-accent" />
        <h2 className="text-lg font-bold text-white">Invoice Options</h2>
      </div>

      {/* Group line items toggle */}
      <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-white">Group similar line items across tickets</p>
            <p className="text-xs text-fieldiq-muted mt-0.5">
              Combine matching descriptions into single line items on the invoice
            </p>
          </div>
          <button
            onClick={() => onGroupLineItemsChange(!groupLineItems)}
            className={`relative w-12 h-7 rounded-full transition-colors ${
              groupLineItems ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                groupLineItems ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Payment terms */}
      <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
        <label className="text-xs text-fieldiq-muted block mb-2">Payment Terms</label>
        <input
          type="text"
          value={paymentTerms}
          onChange={(e) => onPaymentTermsChange(e.target.value)}
          placeholder="Net 30"
          className={inputClass}
        />
      </div>

      {/* Notes */}
      <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
        <label className="text-xs text-fieldiq-muted block mb-2">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          rows={4}
          placeholder="Additional notes for this invoice..."
          className={inputClass}
        />
      </div>
    </div>
  );
}

// ===========================================================================
// STEP 4 — Review
// ===========================================================================
function StepReview({
  customer,
  tickets,
  estimatedSubtotal,
  groupLineItems,
  paymentTerms,
  notes,
}: {
  customer: Customer | null;
  tickets: Ticket[];
  estimatedSubtotal: number;
  groupLineItems: boolean;
  paymentTerms: string;
  notes: string;
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <FileText size={18} className="text-fieldiq-accent" />
        <h2 className="text-lg font-bold text-white">Review Invoice</h2>
      </div>

      {/* Summary card */}
      <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-fieldiq-accent/20 flex items-center justify-center">
            <Building2 size={20} className="text-fieldiq-accent" />
          </div>
          <div>
            <p className="font-bold text-white">{customer?.name || '—'}</p>
            <p className="text-xs text-fieldiq-text">
              {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} selected
            </p>
          </div>
        </div>

        <div className="border-t border-fieldiq-border pt-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-fieldiq-text">Estimated Subtotal</span>
            <span className="text-white font-bold">{fmt(estimatedSubtotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-fieldiq-text">Payment Terms</span>
            <span className="text-white">{paymentTerms || 'None'}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-fieldiq-text">Group Line Items</span>
            <span className="text-white">{groupLineItems ? 'Yes' : 'No'}</span>
          </div>
          {notes && (
            <div className="pt-2 border-t border-fieldiq-border">
              <p className="text-xs text-fieldiq-muted mb-1">Notes</p>
              <p className="text-sm text-white whitespace-pre-wrap">{notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Selected tickets */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <DollarSign size={16} className="text-fieldiq-accent" />
          <h3 className="text-sm font-bold text-white">Selected Tickets</h3>
        </div>
        <div className="space-y-2">
          {tickets.map((ticket) => {
            const ticketNum = ticket.ticketNumber || ticket.ticket_number || ticket.id;
            const rig = ticket.rigName || ticket.rig_name;
            const subtotal = ticket.subtotal || ticket.total || 0;

            return (
              <div
                key={ticket.id}
                className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-white">#{ticketNum}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-fieldiq-text">{ticket.date}</span>
                    {rig && <span className="text-xs text-fieldiq-muted">{rig}</span>}
                  </div>
                </div>
                <span className="text-sm font-bold text-green-400">{fmt(subtotal)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
